import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getAnnouncements, createAnnouncement, uploadAttachments, createPoll } from '../api/apiClient';
import AnnouncementCard from './AnnouncementCard';
import RichTextInput from './RichTextInput';
import socket from '../socket';
import './Announcements.css';

const Announcements = ({ currentUser, isAdmin }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const focusAnnouncementId = location.state?.focusAnnouncementId
    ?? (queryParams.get('announcement') ? Number(queryParams.get('announcement')) : null);
  const focusCommentId = location.state?.focusCommentId
    ?? (queryParams.get('comment') ? Number(queryParams.get('comment')) : null);
  const openCommentForm = location.state?.openCommentForm ?? false;
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: '' });
  const [contentData, setContentData] = useState({ text: '', mentions: [] });
  const contentInputRef = useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const fileInputRef = useRef(null);
  const [pollEnabled, setPollEnabled] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [newIds, setNewIds] = useState(new Set());
  const knownIdsRef = useRef(null);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const response = await getAnnouncements(currentUser?.id);
      const fetched = response.data;

      // On subsequent fetches, detect genuinely new announcements
      if (knownIdsRef.current !== null) {
        const known = knownIdsRef.current;
        const incoming = fetched.filter(a => !known.has(a.id));
        if (incoming.length > 0) {
          const incomingIds = new Set(incoming.map(a => a.id));
          setNewIds(incomingIds);
          setTimeout(() => setNewIds(new Set()), 600);
        }
      }

      knownIdsRef.current = new Set(fetched.map(a => a.id));
      setAnnouncements(fetched);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    fetchAnnouncements();
    socket.on('announcements:changed', fetchAnnouncements);
    return () => socket.off('announcements:changed', fetchAnnouncements);
  }, [fetchAnnouncements]);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setPendingFiles(prev => [...prev, ...newFiles]);
    setFileInputKey(k => k + 1);
  };

  const removeFile = (index) => setPendingFiles(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !contentData.text) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const res = await createAnnouncement({
        title: formData.title,
        content: contentData.text,
        mentions: contentData.mentions,
        author_id: currentUser.id,
        is_admin: isAdmin,
      });

      if (pendingFiles.length > 0) {
        const fd = new FormData();
        pendingFiles.forEach(f => fd.append('files', f));
        await uploadAttachments(res.data.id, fd);
      }

      if (pollEnabled && pollQuestion.trim() && pollOptions.filter(o => o.trim()).length >= 2) {
        await createPoll({
          announcement_id: res.data.id,
          question: pollQuestion.trim(),
          options: pollOptions.filter(o => o.trim()),
        });
      }

      setFormData({ title: '' });
      setContentData({ text: '', mentions: [] });
      contentInputRef.current?.clear();
      setPendingFiles([]);
      setPollEnabled(false);
      setPollQuestion('');
      setPollOptions(['', '']);
      setShowForm(false);
      fetchAnnouncements();
    } catch (error) {
      console.error('Error creating announcement:', error);
      alert('Error creating announcement');
    }
  };

  return (
    <div className="announcements">
      <div className="announcements-header">
        <h2>Announcements</h2>
        {isAdmin && currentUser && currentUser.id && (
          <div className="split-btn" onClick={() => setShowForm(!showForm)}>
            <span className="split-btn-main">
              {showForm ? (
                <>
                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>
                  Cancel
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 11V5M8 5L5.5 7.5M8 5L10.5 7.5"/>
                  </svg>
                  Post Announcement
                </>
              )}
            </span>
            <span className="split-btn-caret">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="2,3 5,7 8,3"/></svg>
            </span>
          </div>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="announcement-form">
          <input
            type="text"
            placeholder="Announcement Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="form-input"
          />
          <RichTextInput
            ref={contentInputRef}
            placeholder="What's your announcement?"
            className="rich-input--form"
            showToolbar
            htmlMode
            onChangeValue={(html, mentions) => setContentData({ text: html, mentions })}
          />
          <input key={fileInputKey} id="announce-file-input" ref={fileInputRef} type="file" multiple hidden onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.txt" />

          {pendingFiles.length > 0 && (
            <ul className="pending-files">
              {pendingFiles.map((f, i) => (
                <li key={i}>
                  <span>{f.name}</span>
                  <button type="button" onClick={() => removeFile(i)}>✕</button>
                </li>
              ))}
            </ul>
          )}

          {pollEnabled && (
            <div className="poll-builder">
              <input
                type="text"
                className="poll-question-input"
                placeholder="Ask a question…"
                value={pollQuestion}
                onChange={e => setPollQuestion(e.target.value)}
                maxLength={200}
              />
              <div className="poll-options-list">
                {pollOptions.map((opt, i) => (
                  <div key={i} className="poll-option-input-row">
                    <input
                      type="text"
                      className="poll-option-input"
                      placeholder={`Option ${i + 1}`}
                      value={opt}
                      onChange={e => { const next = [...pollOptions]; next[i] = e.target.value; setPollOptions(next); }}
                      maxLength={100}
                    />
                    {pollOptions.length > 2 && (
                      <button type="button" className="poll-remove-option" onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}>✕</button>
                    )}
                  </div>
                ))}
              </div>
              {pollOptions.length < 4 && (
                <button type="button" className="poll-add-option-btn" onClick={() => setPollOptions([...pollOptions, ''])}>+ Add option</button>
              )}
            </div>
          )}

          <div className="form-actions-row">
            <label htmlFor="announce-file-input" className="split-btn split-btn--sm" style={{ cursor: 'pointer' }}>
              <span className="split-btn-main">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                </svg>
                Attach files
              </span>
              <span className="split-btn-caret">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="2,3 5,7 8,3"/></svg>
              </span>
            </label>

            <div className={`split-btn split-btn--sm${pollEnabled ? ' split-btn--active' : ''}`} onClick={() => { setPollEnabled(v => !v); setPollQuestion(''); setPollOptions(['', '']); }}>
              <span className="split-btn-main">
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="7" width="3" height="6" rx="0.5"/><rect x="5.5" y="4" width="3" height="9" rx="0.5"/><rect x="10" y="1" width="3" height="12" rx="0.5"/>
                </svg>
                {pollEnabled ? 'Remove Poll' : 'Add Poll'}
              </span>
              <span className="split-btn-caret">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="2,3 5,7 8,3"/></svg>
              </span>
            </div>

            <button type="submit" className="split-btn split-btn--sm split-btn--action">
              <span className="split-btn-main">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Post
              </span>
              <span className="split-btn-caret">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="2,3 5,7 8,3"/></svg>
              </span>
            </button>
          </div>

        </form>
      )}

      <div className="sd-search-wrap">
        <input
          className="sd-search"
          type="text"
          placeholder="Search announcements…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {query ? (
          <button className="sd-search-clear" onClick={() => setQuery('')}>✕</button>
        ) : (
          <span className="sd-search-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="16.5" y1="16.5" x2="22" y2="22" />
            </svg>
          </span>
        )}
      </div>

      {loading ? (
        <p>Loading announcements...</p>
      ) : (() => {
        const q = query.trim().toLowerCase();
        const visible = q
          ? announcements.filter(a =>
              (a.title || '').toLowerCase().includes(q) ||
              (a.content || '').toLowerCase().includes(q) ||
              (a.author_name || '').toLowerCase().includes(q)
            )
          : announcements;
        if (!loading && announcements.length === 0) return <p className="no-announcements">No announcements yet</p>;
        if (q && visible.length === 0) return <p className="no-announcements">No announcements match "{query}"</p>;
        return (
          <div className="announcements-list">
            {visible.map(announcement => {
              const isFocused = announcement.id === focusAnnouncementId;
              return (
                <div
                  key={announcement.id}
                  className={newIds.has(announcement.id) ? 'announcement-new' : ''}
                >
                  <AnnouncementCard
                    announcement={announcement}
                    currentUser={currentUser}
                    isAdmin={isAdmin}
                    onCommentAdded={fetchAnnouncements}
                    onAnnouncementDeleted={fetchAnnouncements}
                    isFocused={isFocused}
                    focusCommentId={isFocused ? focusCommentId : null}
                    autoOpenCommentForm={isFocused ? openCommentForm : false}
                  />
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
};

export default Announcements;
